/**
 * /auth/callback — Auth0 Universal Login redirect target (Phase 2 #5B).
 *
 * Sequence:
 *   1. Consume the ?code=&state= URL params via handleRedirectCallback().
 *   2. Pull the access_token + id_token off the SDK.
 *   3. Exchange the id_token for short-lived Cognito AWS creds.
 *   4. Persist the Auth0 access_token as the API session (CompositeJWTValidator
 *      accepts RS256 too) + the Cognito creds for AWS SDK calls.
 *   5. Redirect to /.
 *
 * If anything fails, show a minimal error + a link back to /login. We don't
 * sanitize the error string to be pretty — Phase 2 dev surface.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

import { getAuth0Client } from "./auth0Client";
import { exchangeAuth0IdTokenForCognitoCreds } from "./cognito";
import { saveCognitoCreds, saveSession } from "./session";

interface DecodedAccessToken {
  sub?: string;
  org_id?: string;
  exp?: number;
  email?: string;
  name?: string;
  "https://re-platform/primary_role"?: string;
}

function decodeJwtPayload(token: string): DecodedAccessToken {
  const [, payload] = token.split(".");
  if (!payload) return {};
  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as DecodedAccessToken;
  } catch {
    return {};
  }
}

export function Callback() {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  // React 19 StrictMode invokes useEffect twice in dev. The Auth0 SDK consumes
  // the URL `state` on the first call, so the second call fails with
  // "Invalid state". A ref guard makes this idempotent per mount.
  const ranRef = useRef(false);

  useEffect(() => {
    // The ref guard alone (no cancelled flag) is what we want: StrictMode
    // dev re-runs the effect, so without the guard handleRedirectCallback
    // gets called twice and the second consumes a state that's already gone
    // ("Invalid state"). With the guard, the async work runs exactly once
    // and is allowed to finish — no cleanup race against navigate().
    if (ranRef.current) return;
    ranRef.current = true;
    void (async () => {
      try {
        const client = getAuth0Client();
        await client.handleRedirectCallback();

        // Checked before anything is stored: a sign-in without an access
        // token must not leave Cognito credentials behind (spec 014).
        const accessToken = await client.getTokenSilently();
        if (!accessToken) {
          throw new Error("Auth0 returned no access token");
        }
        const claims = await client.getIdTokenClaims();
        if (!claims?.__raw) {
          throw new Error("Auth0 returned no id_token");
        }

        // Cognito federation needs the id_token (not access_token) — it
        // validates the OIDC signature against the registered provider.
        const creds = await exchangeAuth0IdTokenForCognitoCreds(claims.__raw);
        saveCognitoCreds(creds);

        // The access_token is what bearer-token API calls use. Decode just
        // enough of its payload to populate SessionMeta — the server still
        // re-validates on every request.
        const decoded = decodeJwtPayload(accessToken);
        if (!decoded.sub || !decoded.org_id || !decoded.exp) {
          throw new Error(
            "Auth0 access_token missing sub/org_id/exp — check the audience config",
          );
        }
        const sub = decoded.sub.includes("|")
          ? decoded.sub.split("|")[1]!
          : decoded.sub;
        saveSession({
          accessToken,
          expiresIn: Math.max(0, decoded.exp - Math.floor(Date.now() / 1000)),
          userId: sub,
          tenantId: decoded.org_id,
          primaryRole: decoded["https://re-platform/primary_role"] ?? "client",
          email: decoded.email ?? "",
          displayName: decoded.name ?? null,
        });
        navigate("/");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Auth0 callback failed");
      }
    })();
  }, [navigate]);

  if (error) {
    return (
      <main className="signin">
        <section className="signin__card">
          <h1>Sign-in failed</h1>
          <p>{error}</p>
          <p>
            <Link to="/login">Back to sign in</Link>
          </p>
        </section>
      </main>
    );
  }
  return (
    <main className="signin">
      <section className="signin__card">
        <h1>Signing you in…</h1>
        <p>One moment while we finish setting up your session.</p>
      </section>
    </main>
  );
}
